import { useBookSkiPassStore } from '@/store/book-ski-pass.store';

export type GtmEcommerceItem = {
  item_id: string;
  item_name: string;
  price: number;
  currency: string;
  quantity?: number;
};

type GtmEventPayload = {
  event: 'view_item' | 'add_to_cart' | 'begin_checkout' | 'purchase';
  ecommerce: {
    currency: string;
    items: GtmEcommerceItem[];
    value?: number;
    transaction_id?: string;
  };
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    __gtmSentEventKeys?: Set<string>;
  }
}

const GTM_DEBUG_STORAGE_KEY = 'gtm_debug';

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  const debugFromStorage = window.localStorage.getItem(GTM_DEBUG_STORAGE_KEY);
  return debugFromStorage === '1' || debugFromStorage === 'true';
}

export type JourneyTrackingFields = {
  booking_ref?: string;
  promocode?: string;
  userId?: string | number;
};

type ResortTrackingPayload =
  | { event: 'user_id_set'; userId: string | number }
  | {
      event: 'resort_selection';
      userId: string | number;
      booking_ref: string;
      promocode?: string;
    };

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function journeyTrackingFields(context?: Partial<JourneyTrackingFields>): JourneyTrackingFields {
  const state = useBookSkiPassStore.getState();
  const bookingRef =
    nonEmpty(context?.booking_ref) ||
    nonEmpty(state.bookingReferenceNumber) ||
    nonEmpty(state.stepOneFormFieldsValues.bookingReference);
  const promoCode = nonEmpty(context?.promocode) || nonEmpty(state.stepOneFormFieldsValues.promoCode);
  const userId =
    context?.userId != null && context.userId !== ''
      ? context.userId
      : state.alps2alpsUserId != null && state.alps2alpsUserId !== ''
        ? state.alps2alpsUserId
        : undefined;

  const fields: JourneyTrackingFields = {};
  if (bookingRef) fields.booking_ref = bookingRef;
  if (promoCode) fields.promocode = promoCode;
  if (userId != null && userId !== '') fields.userId = userId;
  return fields;
}

function pushDataLayer(payload: GtmEventPayload, context?: Partial<JourneyTrackingFields>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  const tracked = {
    ...journeyTrackingFields(context),
    ...payload,
  };
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(tracked as unknown as Record<string, unknown>);
  if (isDebugEnabled()) {
    console.info('[GTM:dataLayer.push]', tracked);
  }
}

export function pushDataLayerOnce(
  key: string,
  payload: GtmEventPayload,
  context?: Partial<JourneyTrackingFields>
): void {
  if (typeof window === 'undefined') return;
  window.__gtmSentEventKeys = window.__gtmSentEventKeys ?? new Set<string>();
  if (window.__gtmSentEventKeys.has(key)) return;
  window.__gtmSentEventKeys.add(key);
  pushDataLayer(payload, context);
}

/** After a valid reference opens /trip-details. Skipped when the Alps2Alps customer id is unknown. */
export function pushResortSelectionEvents(): void {
  if (typeof window === 'undefined') return;
  const fields = journeyTrackingFields();
  if (fields.userId == null || fields.userId === '' || !fields.booking_ref) return;

  window.__gtmSentEventKeys = window.__gtmSentEventKeys ?? new Set<string>();
  if (window.__gtmSentEventKeys.has('user_id_set') && window.__gtmSentEventKeys.has('resort_selection')) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  const userIdSet: ResortTrackingPayload = { event: 'user_id_set', userId: fields.userId };
  const resortSelection: ResortTrackingPayload = {
    event: 'resort_selection',
    userId: fields.userId,
    booking_ref: fields.booking_ref,
    ...(fields.promocode ? { promocode: fields.promocode } : {}),
  };
  if (!window.__gtmSentEventKeys.has('user_id_set')) {
    window.__gtmSentEventKeys.add('user_id_set');
    window.dataLayer.push(userIdSet as unknown as Record<string, unknown>);
    if (isDebugEnabled()) {
      console.info('[GTM:dataLayer.push]', userIdSet);
    }
  }
  if (!window.__gtmSentEventKeys.has('resort_selection')) {
    window.__gtmSentEventKeys.add('resort_selection');
    window.dataLayer.push(resortSelection as unknown as Record<string, unknown>);
    if (isDebugEnabled()) {
      console.info('[GTM:dataLayer.push]', resortSelection);
    }
  }
}

/** Store the loyalty profile id, then send resort events once a booking reference exists. */
export function rememberAlps2AlpsUserId(userId: number | null | undefined): void {
  if (typeof userId !== 'number' || !Number.isFinite(userId)) return;
  useBookSkiPassStore.getState().setAlps2AlpsUserId(userId);
  pushResortSelectionEvents();
}

export function centsToMoney(cents: number): number {
  return Number((cents / 100).toFixed(2));
}